import { useEffect, useMemo, useState } from 'react';
import { useRecoilValue } from 'recoil';

import { PageHead, Pager, SearchBox } from 'views/design-system';
import { PERMISSION_SCREEN, useUserRoles } from 'views/routes-children';
import {
	EsignDratDocState,
	InboxActions,
	InboxAuth,
	InboxLastChangeDate,
	InboxSubject,
	ISignedDocInbox,
	useSignedDocsInbox,
} from 'views/signed-doc-inbox';

import 'views/design-system/tokens.scss';
import 'views/design-system/surface.scss';
import './esign-draft-deck.scss';

const PAGE_SIZE = 15;

/**
 * Draft — production has no separate draft component at all: EsignDraftTable
 * is the same Sentbox machinery filtered to status=draft, action fixed to
 * 'editable'. This page keeps that exactly: real InboxActions renders "Edit
 * And Send" (opens the full-viewport prepare flow) and a one-item kebab
 * ("View"), because that's what the shared component already does for
 * type="editable" — nothing hand-rolled here to drift out of sync.
 */
export const EsignDraftDeck = () => {
	const { data: drafts, isLoaded } = useRecoilValue(EsignDratDocState);
	const { getSignedDocsInbox } = useSignedDocsInbox();
	const { checkUserWritePermission } = useUserRoles();

	const isUserPermissionWrite = useMemo(
		() => checkUserWritePermission(PERMISSION_SCREEN.Draft),
		[checkUserWritePermission]
	);

	const [typed, setTyped] = useState('');
	const [page, setPage] = useState(1);

	useEffect(() => {
		getSignedDocsInbox(false, 'draft');
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const filtered = useMemo(() => {
		const q = typed.trim().toLowerCase();
		if (!q) return drafts ?? [];
		return (drafts ?? []).filter((env: ISignedDocInbox) => {
			const haystack = [env.name, ...(env.recipients ?? []).map(r => `${r.fullName ?? r.name} ${r.email}`)]
				.join(' ')
				.toLowerCase();
			return haystack.includes(q);
		});
	}, [drafts, typed]);

	const pages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
	const shown = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

	return (
		<div className="sv2-scope sv2 esign-draft-deck">
			<PageHead
				title="Draft"
				sub={<>{(drafts ?? []).length} drafts &middot; not yet sent</>}
			/>

			<div className="ctl">
				<SearchBox
					value={typed}
					onChange={v => {
						setTyped(v);
						setPage(1);
					}}
					placeholder="Search subject or recipient"
					title="Searches the subject and every recipient's name and email"
				/>
				<Pager page={page} pages={pages} onChange={setPage} />
			</div>

			<div className="panel" style={{ marginTop: 12 }}>
				<div className="tscroll">
					<table className="stbl dtbl">
						<thead>
							<tr>
								<th className="subcol">Subject</th>
								<th>Face Auth.</th>
								<th>Created At</th>
								<th className="accol">Actions</th>
							</tr>
						</thead>
						<tbody>
							{shown.map((env: ISignedDocInbox) => (
								<tr key={env._id}>
									<td className="od subcol">
										<InboxSubject inbox={env} />
									</td>
									<td className="mid">
										<InboxAuth type={env.authType} />
									</td>
									<td className="mid">
										<InboxLastChangeDate inbox={env} />
									</td>
									<td className="accol">
										<InboxActions
											inbox={env}
											action={'editable' as any}
											inboxType={'editable' as any}
											type={'editable' as any}
											isUserPermissionWrite={isUserPermissionWrite}
										/>
									</td>
								</tr>
							))}
						</tbody>
					</table>
				</div>

				{!isLoaded && <div className="sv2-loading">Loading drafts…</div>}
				{isLoaded && filtered.length === 0 && (
					<div className="sv2-empty">
						<div className="sv2-empty-head">No Draft Document Available</div>
						<div className="sv2-empty-body">You have not created draft document yet.</div>
					</div>
				)}
			</div>
		</div>
	);
};

export default EsignDraftDeck;
